import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { runBatchWebSocket } from '../../apis/batchIssuing.service';
import apis from '../../apis';
import Swal from 'sweetalert2';

const STATIC_TOKEN = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1c2VyIjoidmVsb2NpdHkuYWRtaW5AZXhhbXBsZS5jb20ifQ.vssTp--KThrqP0-E7BOpXfTneXmQUYcv0elKAcXhAVg';

const BATCH_SCRIPT = `#!/usr/bin/env bash

set -euo pipefail

# Execute Velocity Career Labs data loader
npx @velocitycareerlabs/data-loader batchissuing \\
  -x \\
  --legacy \\
  -d did:ion:EiBAcn7-hbbFLIiIaOOIlpbxxG8BT03JVDYFnhDX_tmUGw \\
  -o ./badge.template.json \\
  -c ./csv.template.csv \\
  -p ./temp \\
  -t https://www.lipsum.com/feed/html \\
  -e https://credentialagent.glcredentials.com \\
  -a <TOKEN>`;

const BatchIssuing = () => {
  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState(null);
  const [badgeTemplateFile, setBadgeTemplateFile] = useState(null);
  const token = STATIC_TOKEN;
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [qrCodeBase64, setQrCodeBase64] = useState(null);
  const [qrCodesList, setQrCodesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const wsRef = useRef(null);
  const terminalRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/");
  }, [navigate]);

  const getInitialTerminalContent = useCallback(() => [
    { text: '# Script executed by backend (streaming):', type: 'comment' },
    ...BATCH_SCRIPT.split('\n').map(line => ({ text: line, type: 'script' })),
    { text: '', type: 'output' },
    { text: '# Upload files, enter token, and click Run Batch', type: 'comment' },
  ], []);

  useEffect(() => {
    setTerminalOutput(getInitialTerminalContent());
  }, [getInitialTerminalContent]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  const addTerminalLine = useCallback((text, type = 'output') => {
    setTerminalOutput(prev => [...prev, { text, type }]);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        Swal.fire({ icon: 'warning', title: 'Invalid file', text: 'Please select a CSV file.' });
        return;
      }
      setCsvFile(file);
      addTerminalLine(`$ File selected: ${file.name}`, 'info');
    }
  };

  const handleBadgeTemplateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        Swal.fire({ icon: 'warning', title: 'Invalid file', text: 'Please select a JSON file.' });
        return;
      }
      setBadgeTemplateFile(file);
      addTerminalLine(`$ Badge template selected: ${file.name}`, 'info');
    }
  };

  const handleRunBatch = () => {
    if (!csvFile || !badgeTemplateFile) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Upload CSV and badge template.' });
      return;
    }

    setLoading(true);
    setPrompt(null);
    setQrCodeBase64(null);
    setQrCodesList([]);
    setSearchQuery('');

    const runCommand = `npx @velocitycareerlabs/data-loader batchissuing -x --legacy -d did:ion:... -o ${badgeTemplateFile.name} -c ${csvFile.name} -p ./temp -t ... -e ... -a <token>`;
    addTerminalLine(`$ ${runCommand}`, 'command');
    addTerminalLine(`# WebSocket ${apis.batchIssuing.wsUrl}`, 'comment');
    addTerminalLine('Running batch script (streaming)...', 'info');

    const { ws, sendInput } = runBatchWebSocket(
      csvFile,
      badgeTemplateFile,
      token,
      {
        onOutput: (text) => addTerminalLine(text.replace(/\n$/, ''), 'output'),
        onPrompt: (msg) => setPrompt(msg),
        onDone: async (code, qrBase64, qrcodes) => {
          setLoading(false);
          setPrompt(null);
          if (qrcodes && qrcodes.length > 0) {
            setQrCodesList(qrcodes);
            setQrCodeBase64(qrcodes[0].qrCodeBase64);
          } else if (qrBase64) {
            setQrCodeBase64(qrBase64);
            setQrCodesList([{ email: 'generic', qrCodeBase64: qrBase64 }]);
          } else if (code === 0) {
            // Fallback: fetch QR from API if not in WebSocket payload
            try {
              const res = await fetch(apis.batchIssuing.qrcodeUrl);
              if (res.ok) {
                const blob = await res.blob();
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64 = reader.result?.split(',')[1];
                  if (base64) {
                    setQrCodeBase64(base64);
                    setQrCodesList([{ email: 'generic', qrCodeBase64: base64 }]);
                  }
                };
                reader.readAsDataURL(blob);
              }
            } catch (e) {
              console.error('[BatchIssuing] Failed to fetch QR:', e);
            }
          }
          addTerminalLine('', 'output');
          addTerminalLine(code === 0 ? 'Batch completed.' : `Process exited with code ${code}`, code === 0 ? 'success' : 'error');
          if (code === 0) Swal.fire({ icon: 'success', title: 'Batch Completed', showConfirmButton: false, timer: 1500 });
        },
        onError: (err) => {
          setLoading(false);
          setPrompt(null);
          addTerminalLine(`Error: ${err}`, 'error');
          Swal.fire({ icon: 'error', title: 'Error', text: err });
        },
        onClose: () => setLoading(false)
      }
    );

    wsRef.current = { ws, sendInput };
  };

  const handlePromptChoice = (value) => {
    if (!wsRef.current) return;
    const { sendInput } = wsRef.current;
    sendInput(prompt.id, value);
    addTerminalLine(`→ Selected: ${typeof value === 'number' ? prompt?.options?.[value] : value}`, 'info');
    setPrompt(null);
  };

  const handleClearTerminal = () => {
    setTerminalOutput(getInitialTerminalContent());
    setPrompt(null);
  };

  const handleDownloadSpecificQR = async (base64, email) => {
    if (!base64) return;
    const link = document.createElement('a');
    const safeEmail = email ? email.replace(/[^a-zA-Z0-9@.+-_]/g, '_') : 'generic';
    link.download = `qrcode-${safeEmail}.png`;

    if (base64.startsWith('http')) {
      try {
        const response = await fetch(base64);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('Failed to download QR from S3:', err);
        window.open(base64, '_blank');
      }
    } else {
      link.href = `data:image/png;base64,${base64}`;
      link.click();
    }
  };

  const handleCopyLink = (deeplink) => {
    if (!deeplink) return;
    navigator.clipboard.writeText(deeplink)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Copied!',
          text: 'Deeplink copied to clipboard',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000
        });
      })
      .catch((err) => {
        console.error('Failed to copy deeplink:', err);
      });
  };

  return (
    <div className="card-body py-3 px-md-5 w-100" style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <div className="d-flex gap-0" style={{ height: "calc(100vh - 120px)", minHeight: "500px" }}>
        {/* LEFT: Options & Prompts - 50% */}
        <div
          style={{
            width: "50%",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingRight: "16px",
            borderRight: "1px solid #e0e0e0",
            overflowY: "auto"
          }}
        >
          <h2 style={{ color: "#0b8a0b", marginBottom: "8px", fontSize: "1.5rem" }}>Batch Issuing</h2>

          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #e8e8e8"
            }}
          >
            <div className="mb-3">
              <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Upload CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="form-control form-control-sm"
                style={{ fontSize: "13px" }}
              />
              {csvFile && <small className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>{csvFile.name}</small>}
            </div>
            <div className="mb-3">
              <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Education template (json)</label>
              <input
                type="file"
                accept=".json"
                onChange={handleBadgeTemplateChange}
                className="form-control form-control-sm"
                style={{ fontSize: "13px" }}
              />
              {badgeTemplateFile && <small className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>{badgeTemplateFile.name}</small>}
            </div>
            <button
              onClick={handleRunBatch}
              disabled={loading || !csvFile || !badgeTemplateFile}
              style={{
                backgroundColor: "#0b8a0b",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "14px",
                width: "100%"
              }}
              className="btn"
            >
              {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : "Run Batch"}
            </button>
          </div>

          {/* QR Codes Grid - show after completion */}
          {qrCodesList && qrCodesList.length > 0 && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #e8e8e8",
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#333" }}>
                  Generated QR Codes ({qrCodesList.length})
                </div>
                {qrCodesList.length > 1 && (
                  <input
                    type="text"
                    placeholder="Search by vendorUserId/email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "12px",
                      width: "200px",
                      outline: "none"
                    }}
                  />
                )}
              </div>

              {/* Grid of QR Codes */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "16px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  paddingRight: "4px"
                }}
              >
                {qrCodesList
                  .filter(item =>
                    !searchQuery ||
                    (item.email && item.email.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                  .map((item, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: "#f9f9f9",
                        borderRadius: "8px",
                        border: "1px solid #eaeaea",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "8px"
                      }}
                      className="qr-card"
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#555",
                          wordBreak: "break-all",
                          maxHeight: "36px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: "1.2"
                        }}
                        title={item.email}
                      >
                        {item.email}
                      </div>
                      <div
                        style={{
                          backgroundColor: "#fff",
                          padding: "6px",
                          borderRadius: "4px",
                          border: "1px solid #eee"
                        }}
                      >
                        <img
                          src={item.qrCodeBase64?.startsWith('http') ? item.qrCodeBase64 : `data:image/png;base64,${item.qrCodeBase64}`}
                          alt={item.email}
                          style={{ width: "120px", height: "120px", display: "block" }}
                        />
                      </div>
                      <div className="w-100 d-flex gap-1" style={{ gap: "4px" }}>
                        <button
                          onClick={() => handleDownloadSpecificQR(item.qrCodeBase64, item.email)}
                          style={{
                            flex: 1,
                            backgroundColor: "#0b8a0b",
                            color: "white",
                            border: "none",
                            padding: "6px 4px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600"
                          }}
                          className="btn btn-sm"
                        >
                          Download
                        </button>
                        {item.deeplink && (
                          <button
                            onClick={() => handleCopyLink(item.deeplink)}
                            style={{
                              backgroundColor: "#e0f2fe",
                              color: "#0369a1",
                              border: "none",
                              padding: "6px 4px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600"
                            }}
                            className="btn btn-sm"
                            title="Copy deep link"
                          >
                            Copy
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Prompt choices */}
          {prompt && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: "1px solid #0b8a0b"
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "12px" }}>
                {prompt.message || (prompt.id === 'disclosure-choice'
                  ? 'Use existing or create new disclosure?'
                  : 'Select a disclosure:')}
              </div>
              <div className="d-flex flex-row flex-wrap gap-2">
                {prompt.options?.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePromptChoice(prompt.id === 'select-disclosure' ? idx : opt)}
                    style={{
                      backgroundColor: "#0b8a0b",
                      color: "white",
                      border: "none",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      textAlign: "left"
                    }}
                    className="btn btn-sm"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Terminal - 50% */}
        <div style={{ width: "50%", display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              padding: "10px 16px",
              backgroundColor: "#2d2d2d",
              borderTopRightRadius: "8px",
              borderTopLeftRadius: "8px",
              color: "#ccc",
              fontSize: "13px",
              fontWeight: "500"
            }}
          >
            <span>Terminal</span>
            <button
              onClick={handleClearTerminal}
              className="btn btn-sm"
              style={{ color: "#aaa", padding: "4px 12px", fontSize: "12px", backgroundColor: "transparent", border: "1px solid #555" }}
            >
              Clear
            </button>
          </div>
          <div
            ref={terminalRef}
            style={{
              flex: 1,
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              fontSize: "13px",
              padding: "16px",
              overflowY: "auto",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              border: "1px solid #2d2d2d",
              borderTop: "none",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all"
            }}
          >
            {terminalOutput.map((line, i) => (
              <div
                key={i}
                style={{
                  color: line.type === 'command' ? '#569cd6' : line.type === 'info' ? '#9cdcfe' : line.type === 'error' ? '#f48771' : line.type === 'success' ? '#4ec9b0' : line.type === 'comment' ? '#6a9955' : line.type === 'script' ? '#ce9178' : '#d4d4d4'
                }}
              >
                {line.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchIssuing;
